/**
 * Shared styling for inline multiselect (API path and MultiSelectDropdown).
 * Keeps look and behavior identical for both code paths.
 * Tags stay on one line (no wrap); border/underline matches read-only Tag (grey[200]).
 *
 * Also contains scoped cell state styles (hover, edit, error, disabled)
 * per Figma specs for dl-59430.
 */

import { colors } from 'styles/theme/colors';
import type { CSSProperties } from 'react';

/** Popper modifier config - typed so modifiers array is mutable for MUI compatibility */
type PopperModifier = {
  name: string;
  options?: Record<string, unknown>;
};

const inputBorderGrey = colors.grey[200];

export const MULTISELECT_INPUT_SX = {
  '& .MuiInputBase-root': {
    flexWrap: 'nowrap',
    gap: '4px',
    paddingTop: '4px',
    paddingBottom: '4px',
    overflow: 'hidden',
    minWidth: 0,
    borderBottom: '1px solid',
    borderBottomColor: inputBorderGrey,
    '&::before': {
      borderBottomColor: inputBorderGrey,
    },
    '&::after': {
      borderBottomColor: inputBorderGrey,
    },
    '&:hover': {
      borderBottomColor: inputBorderGrey,
      '&::before': { borderBottomColor: inputBorderGrey },
    },
    '&.Mui-focused': {
      borderBottomColor: inputBorderGrey,
      '&::after': { borderBottomColor: inputBorderGrey },
    },
  },
};

export const MULTISELECT_PAPER_PROPS = {
  paper: {
    sx: {
      marginTop: '4px',
    },
  },
};

export const MULTISELECT_POPPER_MODIFIERS: PopperModifier[] = [
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
    },
  },
  {
    name: 'preventOverflow',
    options: {
      boundary: 'viewport',
      altAxis: true,
      padding: 8,
    },
  },
  {
    name: 'eventListeners',
    options: {
      scroll: true,
      resize: true,
    },
  },
];

export const MULTISELECT_POPPER_PROPS = {
  popper: {
    placement: 'bottom-start' as const,
    popperOptions: {
      modifiers: MULTISELECT_POPPER_MODIFIERS,
    },
  },
};

// ============================================================================
// Cell State Styles (dl-59430 — scoped to multiselect only)
// ============================================================================

const cellBorderSubtle = `0.5px solid ${colors.grey[200]}`;
const cellBorderError = `0.5px solid ${colors.error[500]}`;
const cellRadius = '8px';
const cellPadding = '8px';

export const MULTISELECT_CELL_BASE: CSSProperties = {
  width: '100%',
  minHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: cellRadius,
  padding: `0 ${cellPadding}`,
  boxSizing: 'border-box',
  transition: 'background-color 0.15s, outline-color 0.15s',
};

export const MULTISELECT_CELL_HOVER: CSSProperties = {
  backgroundColor: colors.grey[100],
  outline: cellBorderSubtle,
  outlineOffset: '-0.5px',
  cursor: 'pointer',
};

export const MULTISELECT_CELL_EDITING: CSSProperties = {
  outline: cellBorderSubtle,
  outlineOffset: '-0.5px',
  backgroundColor: '#FFFFFF',
  cursor: 'default',
};

export const MULTISELECT_CELL_ERROR: CSSProperties = {
  outline: cellBorderError,
  outlineOffset: '-0.5px',
};

export const MULTISELECT_CELL_DISABLED: CSSProperties = {
  opacity: 0.5,
  cursor: 'default',
  pointerEvents: 'none',
};

export const MULTISELECT_PLACEHOLDER_STYLE: CSSProperties = {
  color: colors.grey[300],
  fontSize: '14px',
  userSelect: 'none',
};
